import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import './styles/variables.css';
import './styles/Dashboard.css';
import './styles/modern-ui.css';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import Clients from './components/Clients';
import ClientDetail from './components/ClientDetail';
import AddClient from './components/AddClient';
import ClientEdit from './components/ClientEdit';
import JournalActivite from './components/JournalActivite';
import LeadManagementPage from './components/LeadManagementPage';
import Charges from './components/Charges';
import Procedures from './components/Procedures';
import VisaDocumentGenerator from './components/VisaDocumentGenerator';
import FactureDetail from './components/FactureDetail';
import AutoLogoutWarning from './components/AutoLogoutWarning';
import AutoLogoutDemo from './components/AutoLogoutDemo';
import AutoLogoutTest from './components/AutoLogoutTest';
import MiniAutoLogoutTest from './components/MiniAutoLogoutTest';
import ToastDemo from './components/ToastDemo';
import TestAdmissibilite from './components/TestAdmissibilite';
import ImmigrationCalculator from './components/ImmigrationCalculator';
import PSUQCalculator from './components/PSUQCalculator';
// @ts-ignore
import EntreeExpressCalculator from './components/EntreeExpressCalculator';
import { NotificationProvider } from './contexts/NotificationContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ToastProvider } from './components/notifications/ToastProvider';
import { setupAxiosInterceptors, validateToken, forceLogout } from './services/authInterceptor';
import useAutoLogout from './hooks/useAutoLogout';

// Configuration globale d'axios pour l'authentification
const configureAxiosAuth = () => {
  const token = localStorage.getItem('token');
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!localStorage.getItem('token'));
  const [userRole, setUserRole] = useState<string | null>(() => localStorage.getItem('role') || null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  
  // 🔐 Gestion de la déconnexion automatique après 10 minutes d'inactivité
  const handleAutoLogout = () => {
    console.log('🔒 Déconnexion automatique déclenchée - Inactivité de 10 minutes détectée');
    
    // Utiliser la fonction centralisée de déconnexion
    forceLogout();
  };
  
  // Initialiser le hook de déconnexion automatique (9 minutes + 1 minute d'avertissement)
  const { setAuthenticationStatus, showWarning, continueSession, forceLogout: forceAutoLogout } = useAutoLogout(
    9 * 60 * 1000, // 9 minutes avant d'afficher l'avertissement
    handleAutoLogout,
    60 // 60 secondes d'avertissement
  );
  
  // Initialiser l'application et valider le token
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Configurer les intercepteurs axios
        setupAxiosInterceptors();
        
        const token = localStorage.getItem('token');
        if (token) {
          // Valider le token au démarrage
          const isTokenValid = await validateToken();
          
          if (isTokenValid) {
            const savedRole = localStorage.getItem('role');
            if (savedRole) setUserRole(savedRole);
            setIsAuthenticated(true);
            setAuthenticationStatus(true); // Marquer comme authentifié pour le timer
            configureAxiosAuth();
          } else {
            // Token invalide, forcer la déconnexion
            setIsAuthenticated(false);
            setUserRole(null);
            setAuthenticationStatus(false); // Marquer comme non authentifié
          }
        } else {
          setIsAuthenticated(false);
          setUserRole(null);
          setAuthenticationStatus(false); // Marquer comme non authentifié
        }
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error);
        setIsAuthenticated(false);
        setUserRole(null);
        setAuthenticationStatus(false); // Marquer comme non authentifié
      } finally {
        setIsInitializing(false);
      }
    };

    initializeApp();
  }, []);
  
  const handleLogin = async (email: string, password: string) => {
    // Tentative d'appel au backend réel
    try {
      const { loginUser } = await import('./services/usersAPI');
      const response: any = await loginUser(email, password);
      if (response && response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('sessionStartTime', Date.now().toString()); // Enregistrer le début de session
        const backendRole = response.user.role;
        const uiRole = backendRole === 'admin' ? 'administrateur' : backendRole;
        const userName = response.user.nom_complet || response.user.nom || response.user.name || 'Utilisateur';
        const userId = response.user.id || response.user.userId || response.userId;
        
        setUserRole(uiRole);
        localStorage.setItem('role', uiRole);
        localStorage.setItem('userName', userName);
        localStorage.setItem('userId', userId ? userId.toString() : '');
        localStorage.setItem('entrepriseName', response.user.entreprise?.nom || '');
        localStorage.setItem('entrepriseId', response.user.entreprise?.id?.toString() || '');
        window.dispatchEvent(new Event('tenant-settings-refresh'));
        
        // Configurer axios avec le nouveau token
        configureAxiosAuth();
        
        setIsAuthenticated(true);
        setAuthenticationStatus(true); // Démarrer le timer de déconnexion automatique
        return true;
      }
    } catch (e) {
      console.warn('Backend login failed, fallback to mock.', e);
    }

    // ----- Fallback mock avec noms spécifiques (DÉVELOPPEMENT UNIQUEMENT) -----
    // ⚠️ SÉCURITÉ: Ces credentials doivent être SUPPRIMÉS en production
    if (process.env.NODE_ENV !== 'production') {
      if (email === 'admin@immigration.ca' && password === 'admin123') {
        localStorage.setItem('token', 'mock-token');
        localStorage.setItem('sessionStartTime', Date.now().toString()); // Enregistrer le début de session
        localStorage.setItem('role', 'administrateur');
        localStorage.setItem('userName', 'Administrateur');
        localStorage.setItem('userId', '1');
        setUserRole('administrateur');
        setIsAuthenticated(true);
        setAuthenticationStatus(true); // Démarrer le timer
        return true;
      }
      if (email === 'marie.tremblay@immigration.ca' && password === 'conseillere123') {
        localStorage.setItem('token', 'mock-token');
        localStorage.setItem('sessionStartTime', Date.now().toString());
        localStorage.setItem('role', 'conseillere');
        localStorage.setItem('userName', 'Marie T.');
        localStorage.setItem('userId', '2');
        setUserRole('conseillere');
        setIsAuthenticated(true);
        setAuthenticationStatus(true); // Démarrer le timer
        return true;
      }
    }
    
    // ✅ Tous les comptes de test ont été supprimés pour la sécurité en production
    // Seule l'authentification via le backend est autorisée
    return false;
  };
  
  const handleLogout = async () => {
    console.log('🔓 Déconnexion manuelle déclenchée');
    
    // Appeler l'API backend pour enregistrer la déconnexion
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post('/api/users/logout', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('✅ Déconnexion enregistrée dans les journaux d\'activité');
      }
    } catch (error) {
      console.warn('⚠️ Erreur lors de l\'enregistrement de la déconnexion:', error);
    }
    
    // Nettoyer le localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userName');
    localStorage.removeItem('entrepriseName');
    localStorage.removeItem('entrepriseId');
    localStorage.removeItem('userId');
    localStorage.removeItem('sessionStartTime'); // Nettoyer aussi le temps de début de session
    localStorage.removeItem('activeModule'); // 🔄 NOUVEAU : Nettoyer l'état de navigation
    
    // Nettoyer les headers axios
    delete axios.defaults.headers.common['Authorization'];
    
    // Réinitialiser l'état
    setIsAuthenticated(false);
    setUserRole(null);
    
    // Arrêter le timer de déconnexion automatique
    setAuthenticationStatus(false);
  };
  
  // Affichage de chargement pendant l'initialisation
  if (isInitializing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>Vérification de la session...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Auth onLogin={handleLogin} />;
  }

  return (
    <>
      <HashRouter>
        <SettingsProvider>
          <NotificationProvider> { /* Envelopper les Routes avec NotificationProvider */ }
            <ToastProvider position="top-right"> { /* Nouveau système de toast */ }
              <Routes>
                <Route path="/" element={<Dashboard userRole={userRole || 'visiteur'} userName={localStorage.getItem('userName') || 'Utilisateur'} entrepriseName={localStorage.getItem('entrepriseName') || ''} onLogout={handleLogout} />}>
                  {/* Routes imbriquées pour conserver le layout Dashboard */}
                  <Route
                    path="client/nouveau"
                    element={
                      userRole === 'secretaire'
                        ? <Navigate to="/" replace />
                        : <AddClient />
                    }
                  />
                <Route
                  path="client/modifier/:id"
                  element={
                    userRole === 'secretaire'
                      ? <Navigate to="/" replace />
                      : <ClientEdit />
                  }
                />
              </Route>

              {/* Autres routes existantes (inchangées) */}
              <Route path="/leads" element={<LeadManagementPage />} />
              <Route path="/clients" element={<Clients userRole={userRole || 'visiteur'} onEditClient={(id: any) => console.log('Edit client:', id)} />} />
              <Route path="/client/:id" element={<ClientDetail />} />
              <Route path="/procedures" element={<Procedures />} />
              <Route path="/admissibilite" element={<ImmigrationCalculator />} />
              <Route path="/entree-express" element={<EntreeExpressCalculator />} />
              <Route path="/pstq-quebec" element={<PSUQCalculator />} />
              <Route path="/documents" element={<VisaDocumentGenerator />} />
              <Route path="/facturation/:id" element={<FactureDetail />} />
              <Route
                path="/journaux"
                element={
                  ['administrateur', 'super_admin'].includes(userRole || '')
                    ? <JournalActivite />
                    : <Navigate to="/" replace />
                }
              />
              <Route path="/charges" element={<Charges />} />
              {/* Routes de démo pour le développement */}
              <Route path="/demo-logout" element={<AutoLogoutDemo />} />
              <Route path="/test-logout" element={<AutoLogoutTest />} />
              <Route path="/mini-test" element={<MiniAutoLogoutTest />} />
              <Route path="/toast-demo" element={<ToastDemo />} />
              <Route path="/test-admissibilite" element={<TestAdmissibilite />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </ToastProvider> { /* Fermeture du ToastProvider */ }
        </NotificationProvider>
        </SettingsProvider>
      </HashRouter>
      
      {/* 🔐 Composant d'avertissement de déconnexion automatique */}
      <AutoLogoutWarning
        isVisible={showWarning}
        onContinue={continueSession}
        onLogout={forceAutoLogout}
        warningTime={60}
      />
    </>
  );
}

export default App;
